import { useState, type FC } from 'react'
import { useTranslation } from 'react-i18next'
import Switch from '../common/Switch.tsx'

interface ExpressiveVoiceToggleProps {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  disabled?: boolean
  tooltipPosition?: 'left' | 'right' | 'top' | 'bottom'
}

const tooltipPositionClasses = {
  left: 'left-0 top-full',
  right: 'right-0 top-full',
  top: 'bottom-full left-1/2 -translate-x-1/2',
  bottom: 'top-full left-1/2 -translate-x-1/2',
} as const

const tooltipSpacingClasses = {
  left: 'mt-2',
  right: 'mt-2',
  top: 'mb-2',
  bottom: 'mt-2',
} as const

const ExpressiveVoiceToggle: FC<ExpressiveVoiceToggleProps> = ({
  checked,
  onCheckedChange,
  disabled = false,
  tooltipPosition = 'right',
}) => {
  const { t } = useTranslation()
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)

  return (
    <div
      className={`inline-flex items-center gap-2 text-xs font-semibold ${
        disabled ? 'text-zinc-400' : 'text-white'
      }`}
    >
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
      />
      <span>{t('ls_generic_expressive_voice')}</span>
      <div className="relative inline-flex items-center">
        <button
          type="button"
          className="inline-flex h-4 w-4 items-center justify-center text-zinc-400 transition-colors hover:text-zinc-300 focus:outline-none focus-visible:text-zinc-300"
          aria-label={t('ls_generic_expressive_voice_tooltip')}
          onMouseEnter={() => setIsTooltipOpen(true)}
          onMouseLeave={() => setIsTooltipOpen(false)}
          onFocus={() => setIsTooltipOpen(true)}
          onBlur={() => setIsTooltipOpen(false)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M6.66667 1.33333C3.72115 1.33333 1.33333 3.72115 1.33333 6.66667C1.33333 9.61218 3.72115 12 6.66667 12C9.61218 12 12 9.61218 12 6.66667C12 3.72115 9.61218 1.33333 6.66667 1.33333ZM0 6.66667C0 2.98477 2.98477 0 6.66667 0C10.3486 0 13.3333 2.98477 13.3333 6.66667C13.3333 10.3486 10.3486 13.3333 6.66667 13.3333C2.98477 13.3333 0 10.3486 0 6.66667ZM5.60526 3.66667C5.60526 3.29848 5.90374 3 6.27193 3H6.2786C6.64679 3 6.94527 3.29848 6.94527 3.66667C6.94527 4.03486 6.64679 4.33333 6.2786 4.33333H6.27193C5.90374 4.33333 5.60526 4.03486 5.60526 3.66667ZM4.66667 6C4.66667 5.63181 4.96514 5.33333 5.33333 5.33333H6.66667C7.03486 5.33333 7.33333 5.63181 7.33333 6V8.66667H8C8.36819 8.66667 8.66667 8.96514 8.66667 9.33333C8.66667 9.70152 8.36819 10 8 10H5.33333C4.96514 10 4.66667 9.70152 4.66667 9.33333C4.66667 8.96514 4.96514 8.66667 5.33333 8.66667H6V6.66667H5.33333C4.96514 6.66667 4.66667 6.36819 4.66667 6Z"
              fill="#A1A1AA"
            />
          </svg>
        </button>
        {isTooltipOpen && (
          <div
            className={`absolute ${tooltipPositionClasses[tooltipPosition]} ${tooltipSpacingClasses[tooltipPosition]} z-10 w-56 rounded-md border border-zinc-700 bg-neutral-900 px-3 py-2 text-left text-xs font-medium leading-5 text-white shadow-lg`}
          >
            {t('ls_generic_expressive_voice_tooltip')}
          </div>
        )}
      </div>
    </div>
  )
}

export default ExpressiveVoiceToggle
